import React, { useState, useSyncExternalStore } from "react";
import { useMsal } from "@azure/msal-react";
import {
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Badge,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  Box,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { loginRequest } from "../authConfig";
import { AppDispatch } from "../store/store";
import { useDispatch } from "react-redux";
import { stopImpersonationAction } from "../store/modules/admin/adminActions";
import { logOut } from "../utils/auth";
import { getSessionVersion, subscribeToSession } from "../auth/session";

interface UserAccountMenuProps {
  isImpersonating: boolean;
  impersonatedUserEmail?: string | null;
}

/**
 * Identity and sign-out only. Admin tools live under /admin; the one exception is stopping
 * impersonation, which is needed mid-task and should not require navigating away.
 */
const UserAccountMenu: React.FC<UserAccountMenuProps> = ({
  isImpersonating,
  impersonatedUserEmail,
}) => {
  const { instance } = useMsal();
  // Native (affiliate) sign-in raises no MSAL events, so MsalProvider's account state can be stale.
  // Read the instance directly and re-render on session changes, as Routes does.
  useSyncExternalStore(subscribeToSession, getSessionVersion);
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
  const isAuthenticated = !!account;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleLogin = () => {
    setAnchorEl(null);
    instance.loginRedirect(loginRequest);
  };

  const handleLogout = async () => {
    setAnchorEl(null);

    try {
      // Clears Redux, persisted state and storage, then signs out of the path the user signed in with.
      await logOut(instance);
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleStopImpersonating = async () => {
    setAnchorEl(null);

    try {
      await dispatch(stopImpersonationAction());
      if (window.location.pathname !== "/") {
        window.location.href = "/";
      }
    } catch (error) {
      console.error("Failed to stop impersonation:", error);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        size="small"
        sx={{ color: "white" }}
        title={isAuthenticated ? "Account" : "Sign in"}
      >
        <Badge color="error" variant="dot" invisible={!isImpersonating}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PersonIcon />
            <ArrowDropDownIcon fontSize="small" />
          </Box>
        </Badge>
      </IconButton>

      {isMobile ? (
        <Dialog open={open} onClose={handleMenuClose} fullWidth maxWidth="xs">
          <DialogTitle>{isAuthenticated ? "Account" : "Sign in"}</DialogTitle>
          <DialogContent>
            {isAuthenticated ? (
              <>
                {account && (
                  <Typography variant="body2" gutterBottom>
                    Logged in as: {account.username || account.name}
                  </Typography>
                )}
                {isImpersonating && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Currently impersonating
                    </Typography>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      {impersonatedUserEmail || ""}
                    </Typography>
                    <Button onClick={handleStopImpersonating} color="error" variant="contained" fullWidth>
                      Stop impersonation
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              <Button onClick={handleLogin} variant="outlined" fullWidth>
                Sign in
              </Button>
            )}
          </DialogContent>
          {isAuthenticated && (
            <DialogActions>
              <Button onClick={handleMenuClose}>Cancel</Button>
              <Button onClick={handleLogout} color="error">
                Sign out
              </Button>
            </DialogActions>
          )}
        </Dialog>
      ) : (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          disableAutoFocusItem
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {isAuthenticated
            ? [
                account && (
                  <MenuItem key="account-info" disabled>
                    <Typography variant="body2" color="text.primary">
                      {account.username || account.name}
                    </Typography>
                  </MenuItem>
                ),
                isImpersonating && (
                  <MenuItem key="impersonation-info" disabled>
                    <Typography variant="body2" color="text.secondary">
                      Impersonating: {impersonatedUserEmail || ""}
                    </Typography>
                  </MenuItem>
                ),
                isImpersonating && (
                  <MenuItem key="stop-impersonating" onClick={handleStopImpersonating}>
                    <Typography color="error">Stop impersonation</Typography>
                  </MenuItem>
                ),
                <Divider key="sign-out-divider" />,
                <MenuItem key="logout" onClick={handleLogout}>
                  <Typography color="error">Sign out</Typography>
                </MenuItem>,
              ]
            : [
                <MenuItem key="login" onClick={handleLogin}>
                  <Typography color="text.primary">Sign in</Typography>
                </MenuItem>,
              ]}
        </Menu>
      )}
    </>
  );
};

export default UserAccountMenu;
